// MyLocationCreate.js
import React, { useCallback, useEffect, useState, useRef } from 'react';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { GenerateThink5whysLocationCode } from '../../components/GenerateThink5whysCode';

const MyLocationCreate = () => {
  const authState = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false); 
  const [updateSuccess, setUpdateSuccess] = useState(false); 

  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState(''); 
 
  const [creatingLocation, setCreatingLocation] = useState(false); 
   
  const [verifyParentLocationSuccess, setVerifyParentLocationSuccess] = useState(false); 

  const [errors, setErrors] = useState({});
  
  const [parentLocationCreatorName, setParentLocationCreatorName] = useState(''); 
  const [autoLocationCode, setAutoTaskCode] = useState(GenerateThink5whysLocationCode());

  const userId = authState.user._id;
  const [formData, setFormData] = useState({
    createdBy: authState.user._id,
    creatorCode: authState.user.think5whyscode,
    creatorName: authState.user.name,
    locationType: 'FAC', 
    locationCode: autoLocationCode, 
    locationTitle: '',
    locationDesc: '',
    locationDos: '',
    locationDont: '',
    locationMicroPage: '',
    myLocationRemarks: '',
    dateCreated: new Date(),
    isActive: 1,
    isDeleted: 0,
    masterLocationCode: '',
    masterLocationTitle: '',
    masterLocationCreator: '',
    isLevel: false,
    entityCode: '',
    myLocationMembers: null,
  });

  const [createClicked, setCreateClicked] = useState(false);

  const handleCreateClick = () => {
    if(formData.locationTitle==='')
      {
        errors.locationTitle="Title Must Not Be Blank";
        return;
      }
    setCreatingLocation(true);
    fetchLocationCreate();
  };

  const fetchLocationCreate = useCallback(() => {
    // console.log('userId:', userId);
  
    try {
      const config = {
        url: "/mylocations",
        method: 'post',
        data: formData,
        headers: { Authorization: authState.token },
      };
  
      // console.log('Creating location with config:', config);
  
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // console.log("Location created successfully:", response);
            setCreateSuccess(true);
            setCreateSuccessMsg("Location Created Successfully");
            errors.locationTitle='';
          } else {
            console.error("Failed to create profile:", response);
            setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
          }
        })
        .catch(error => {
          console.error("Error creating profile:", error);
          setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
        });
    } catch (error) {
      console.error('Error in fetchProfileCreate:', error);
      setCreateSuccessMsg("Sorry! Profile Not Created. Please retry.");
    }
  }, [userId, formData, authState, fetchData, setCreateSuccess, setCreateSuccessMsg]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchLocationCreate, setCreateSuccess, setCreateSuccessMsg]);


const handleVerifyParentLocationClick = () => {
    // console.log("handleVerifyParentLocationClick", formData);

    setVerifyParentLocationSuccess(true);
    
    // Validate location Code
    if (/^TT[a-zA-Z0-9]{12}$/.test(formData.masterLocationCode)) {
        if (formData.masterLocationCode === formData.locationCode) {
            setErrors(`Master Location Code is ${formData.masterLocationCode} and Your Location Code is ${formData.locationCode}. Both must be different!`);
            return;
          } 
          fetchParentLocationCode(formData.locationCode);
    } else {
        setErrors('Please enter a valid 14-character Think5Whys Location Code starting with TT.');
    }
};


const fetchParentLocationCode = useCallback(() => {
  // console.log("formData.masterLocationCode for API call ", formData.masterLocationCode);
  const config = {
      url: "/mylocations/verifylocationcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { locationCode: formData.masterLocationCode }
  };

  fetchData(config)
      .then(response => {
        // console.log("API Response: ", response); 
          if (response && response.status) {
              // console.log("formData.masterLocationCode found ", response);
              setParentCodeStatus(true);
              setParentLocationTitle(response.locationcodeinfo.locationTitle);
              setParentLocationCreatorName(response.locationcodeinfo.creatorName);
              setParentLocationStatusMsg("Parent Location Code found. 😊");
              // console.log("Payee Code found");
          } else {
              // console.log("Payee Code Not found");
              setParentLocationStatusMsg(`Parent Location Code ${formData.masterLocationCode} not found! 😔 Make sure the Parent Location Code is valid.`);
          }
      })
      .catch(error => {
          // console.log("Parent Location Code Not found!", error);
          setParentLocationStatusMsg("Error in getting data");
      });
}, [fetchData, authState.token]);


useEffect(() => {
  if (!authState.isLoggedIn) return;
}, [authState.isLoggedIn, fetchParentLocationCode]);


  const handleBackToMyGroupsListClick = () => {
    navigate('/myprofile-locations');
  };

  const handleUpdateClick = async () => {
    try {
      const response = await fetch(`/mylocations/${formData._id}`, {
        method: 'put',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        // Update state or handle success as needed
        // console.log('Location updated successfully.');
      } else {
        console.error('Failed to update location:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating location:', error.message);
    }
  };

  return (
    <MainLayout>
      <div className='text-lg mt-12 mx-8 border-b border-b-gray-300 text-white'>
        <h2>Profile - Location / Groups Creation</h2>
      </div>
      <div className="m-auto my-2 p-2 border-2 shadow-md rounded-md md:w-7/12 w-full text-white text-sm">
        <div className="flex flex-col justify-left mt-2 items-center">
          <div>
            <p className='text-sm text-yellow-200 mr-2'>Think5Whys Location Code: {autoLocationCode} </p>
          </div>
          <div>
            <p className='text-sm text-white mb-1 mt-2'>Input Location Title: </p>
          </div>          
          <div className='w-6/12 flex flex-row mt-2'>
            <input
              type="text"
              className="profile-input text-black"
              style={{
                border: '1px solid #ccc',
                padding: '8px',
                width: '100%',
                boxSizing: 'border-box',
              }} 
              value={formData.locationTitle}
              onChange={(e) => setFormData({ ...formData, locationTitle: e.target.value })}
              placeholder="Enter location title"
            />
          </div>
          <div>
            <button
              className='bg-purple-700 mt-2'
              onClick={handleCreateClick}
              disabled={createSuccess}
              style={{ maxWidth: '150px' }}
            >
              Create Location
            </button>
            <br/>
            {errors.locationTitle !== '' && (
              <p className='text-red-500 text-lg'>{errors.locationTitle}</p>
            )}
          </div>
        </div>
        {createSuccess && (
          <div className='text-sm'>
            <div className="mt-2 border-t">
              <p className='text-lg text-green-400'>Location {formData.locationTitle} Created Successfully!</p>
              <p className='text-lg text-orange-400'>Fill the other details!</p>
            </div>
            <div className="flex flex-col mt-1 text-sm">
              <label>Location Type:</label>
              <select className="profile-select text-black" value={formData.locationType} onChange={(e) => setFormData({ ...formData, locationType: e.target.value })}>
                <option value={"FAC"}>Factory</option>
                <option value={"MAC"}>Machine</option>
                <option value={"BRA"}>Branch</option>
                <option value={"BUI"}>Building</option>
                <option value={"SCH"}>School</option>
                <option value={"COL"}>College</option>
                <option value={"UNI"}>University</option>
                <option value={"OFF"}>Office</option>
                <option value={"SHO"}>Shop</option>
                <option value={"LOC"}>Location</option>
                <option value={"IND"}>Indoor</option>
                <option value={"OUT"}>Outdoor</option>
                <option value={"OTH"}>Others</option>
              </select>
              {errors.locationType && <span className="error">{errors.locationType}</span>}
          </div>
          <div className="flex flex-col">
            <label>Location Description</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.locationDesc}
              onChange={(e) => setFormData({ ...formData, locationDesc: e.target.value })}
              placeholder="Enter location description"
            />
          </div>
          <div className="flex flex-col">
            <label>Location Do's</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.locationDos}
              onChange={(e) => setFormData({ ...formData, locationDos: e.target.value })}
              placeholder="Enter location do's"
            />
          </div>
          <div className="flex flex-col">
            <label>Location Don'ts</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.locationDont}
              onChange={(e) => setFormData({ ...formData, locationDont: e.target.value })}
              placeholder="Enter location don'ts"
            />
          </div>
          <div className="flex flex-col">
            <label>Micro Page Link</label>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '90%'
              }}
              value={formData.locationMicroPage}
              onChange={(e) => setFormData({ ...formData, locationMicroPage: e.target.value })}
              placeholder="Enter micro page link"
            />
          </div>
          <div className="flex flex-col">
            <label>Location Remarks</label>
            <textarea
              className="profile-textarea"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                maxWidth: '650px', 
                maxHeight: '150px' 
              }}
              value={formData.myLocationRemarks}
              onChange={(e) => setFormData({ ...formData, myLocationRemarks: e.target.value })}
              placeholder="Enter location remarks"
            />
          </div>
          <div className="flex flex-col">
            <p>Parent Location</p>
            <input
              type="text"
              className="profile-input"
              style={{
                border: '1px solid #ccc',
                padding: '2px',
                backgroundColor: '#f0f0f0', 
                color: '#000000',
                width: '150px'
              }}
              value={formData.masterLocationCode}
              onChange={(e) => setFormData({ ...formData, masterLocationCode: e.target.value })}
              placeholder="Enter parent location code"
            />
            {/* <button
              className='bg-orange-700 mt-2'
              onClick={handleVerifyParentLocationClick}
              disabled={verifyParentLocationSuccess}
              style={{ maxWidth: '250px' }}
            >
              Verify Parent Location
            </button> */}
          </div>
          <div className="flex flex-col">
              <label>
              <input className='mr-4' type="checkbox" checked={formData.isLevel} onChange={(e) => setFormData({ ...formData, isLevel: e.target.checked })} />
              Location Has Hierarchy?
              </label>
          </div>
          <div className="flex justify-center mt-4">
            <button
              className='bg-purple-700'
              onClick={handleUpdateClick}
              style={{ maxWidth: '150px' }}
            >
              Update
            </button>
            <button
              className='bg-purple-700 ml-4'
              onClick={handleBackToMyGroupsListClick}
              style={{ maxWidth: '250px' }}
            >
              Back To My Location / Group List
            </button>
          </div>
        </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MyLocationCreate;
